import {
  DestroyRef,
  effect,
  inject,
  Injectable,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { CookiesModalComponent } from 'src/app/features/auth/components/cookies-modal/cookies-modal.component';
import { QueryParamName } from 'src/app/shared/enums/query-param-name.enum';
import { RoutePath } from 'src/app/shared/enums/route-path.enum';
import { AnnotationListsStore } from '../store/annotation-lists.store';
import { AnnotationTypesStore } from '../store/annotation-types.store';
import { AnnotationsStore } from '../store/annotations.store';
import { PlaylistsStore } from '../store/playlists.store';
import { RecordingStore } from '../store/recording.store';
import { RouteStore } from '../store/route.store';
import { UserStore } from '../store/user.store';
import { CookieConsentService } from './cookie-consent.service';

const COOKIE_MODAL_DEBOUNCE = 500;

/**
 * The side effects a navigation triggers: loading the resources a match or clip
 * view needs, and asking for cookie consent once the user is somewhere that can
 * show the modal.
 *
 * These used to live in RouteStore's onInit, which made that store both a
 * read-model of the router and the thing that pushed data into six domain
 * stores. Those stores read RouteStore themselves, so the dependency ran both
 * ways and the store had to resolve three of them through an Injector at call
 * time to survive construction (NG0200).
 *
 * Sitting above the stores instead, this service depends on RouteStore and on
 * the domain stores, and neither of them depends on it - so the cycle is gone
 * and RouteStore is back to describing the route and nothing else. Adding a
 * section that loads on navigation is now an edit here, not an edit to the
 * store every other store reads.
 *
 * Started by provideAppInitializer in main.ts; nothing injects it for a value.
 */
@Injectable({ providedIn: 'root' })
export class RouteEffectsService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly cookieConsent = inject(CookieConsentService);

  private readonly routeStore = inject(RouteStore);
  private readonly userStore = inject(UserStore);
  private readonly annotationTypesStore = inject(AnnotationTypesStore);
  private readonly annotationListsStore = inject(AnnotationListsStore);
  private readonly annotationsStore = inject(AnnotationsStore);
  private readonly playlistsStore = inject(PlaylistsStore);
  private readonly recordingStore = inject(RecordingStore);

  constructor() {
    /* Old getRecordingResources$: load match resources when entering the
     * played/live match subsections of recordings or calendar.
     *
     * RouteStore replaces both signals on every NavigationEnd, so this runs
     * once per navigation even when the URL did not change. The loads are
     * untracked: they read store state of their own, and tracking that would
     * re-run this effect on the data it just fetched. */
    effect(() => {
      const pathSegments = this.routeStore.pathSegments();
      const params = this.routeStore.params();

      untracked(() => this.loadRouteResources(pathSegments, params));
    });

    /* Old showCookieModal: ask for cookie consent once per browser. */
    toObservable(this.routeStore.path)
      .pipe(
        filter(path => path !== '' && path !== RoutePath.Privacy),
        debounceTime(COOKIE_MODAL_DEBOUNCE),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.askForCookieConsent());
  }

  private loadRouteResources(
    pathSegments: string[],
    params: { [path: string]: string },
  ): void {
    const [, section, subsection] = pathSegments;

    if (
      (section === RoutePath.Recordings || section === RoutePath.Calendar) &&
      (subsection === RoutePath.PlayedMatch ||
        subsection === RoutePath.LiveMatch)
    ) {
      const id = params[QueryParamName.RecordingId];

      this.annotationTypesStore.get();
      this.recordingStore.get(id);
      this.annotationsStore.getAnnotations(id);
      this.annotationListsStore.getRecordingsAnnotationLists({
        recordingId: id,
      });
    }

    if (section === RoutePath.Playlists && subsection === RoutePath.Clip) {
      this.annotationsStore.getAnnotations(params['annotationId']);
      this.playlistsStore.getPlaylistsRecordings(params['playlistId']);
    }
  }

  private async askForCookieConsent(): Promise<void> {
    const userId = this.userStore.userId();
    if (!userId || this.cookieConsent.isAnswered()) {
      return;
    }

    const cookieDialog = this.dialog.open(CookiesModalComponent);
    const userResponse = await firstValueFrom(cookieDialog.afterClosed());

    this.cookieConsent.set(!!userResponse);
  }
}
